import React from "react";
import { AbsoluteFill, Sequence, useVideoConfig } from "remotion";
import { computeLayout, timelineSchema, type Timeline } from "@veyso/core";
import { AssetBaseContext } from "./assets.js";
import { AudioTracks } from "./components/AudioTracks.js";
import { CaptionLayer } from "./components/Captions.js";
import { FontGate } from "./components/Fonts.js";
import { TransitionWrapper } from "./components/Transitions.js";
import { BodySection } from "./sections/Body.js";
import { BookendSection } from "./sections/Bookend.js";

/**
 * A type alias rather than an interface: Remotion constrains composition props
 * to `Record<string, unknown>`, which an interface cannot satisfy structurally.
 */
export type VeysoCompositionProps = {
  timeline: Timeline;
  /** Where asset keys resolve from. The editor and the renderer differ here. */
  assetBaseUrl?: string;
  /**
   * Overrides which proxy the *preview* plays, for the editor's quality tiers.
   * Ignored while rendering, which always uses the master — so this can never
   * change the encoded output.
   */
  previewProxyKey?: string;
};

/**
 * The one composition. The editor preview and the final render both mount this
 * component with the same timeline, which is what guarantees that what the user
 * approves is what gets encoded.
 *
 * No provider is ever called from inside here: every asset already exists by the
 * time a timeline is valid, so rendering is deterministic and offline.
 */
export const VeysoComposition: React.FC<VeysoCompositionProps> = ({
  timeline,
  assetBaseUrl = "/assets",
  previewProxyKey,
}) => {
  // The rate Remotion is actually counting frames at, which is not always the
  // timeline's own — rendering a 60fps project at 30 is a supported choice.
  const { fps } = useVideoConfig();
  const layout = computeLayout(timeline, fps);
  const { head, body, end } = timeline.sections;
  const headToBody = timeline.transitions.headToBody;
  const bodyToEnd = timeline.transitions.bodyToEnd;

  const headOverlap = layout.headToBody?.durationInFrames ?? 0;
  const endOverlap = layout.bodyToEnd?.durationInFrames ?? 0;
  // Only timelines built before original audio became its own asset reach this;
  // for anything newer the track has a file and AudioTracks plays it.
  const legacyOriginalAudio = timeline.audio.find(
    (track) => track.role === "original" && track.enabled && !track.assetKey,
  );

  return (
    <AssetBaseContext.Provider value={assetBaseUrl}>
      <AbsoluteFill style={{ backgroundColor: "#05070d" }}>
        <FontGate timeline={timeline} />
        {head && layout.head ? (
          <Sequence from={layout.head.from} durationInFrames={layout.head.durationInFrames} name="HEAD">
            <TransitionWrapper
              durationInFrames={layout.head.durationInFrames}
              exit={headToBody ? { transition: headToBody, frames: headOverlap } : undefined}
            >
              <BookendSection scene={head} />
            </TransitionWrapper>
          </Sequence>
        ) : null}

        <Sequence from={layout.body.from} durationInFrames={layout.body.durationInFrames} name="BODY">
          <TransitionWrapper
            durationInFrames={layout.body.durationInFrames}
            enter={head && headToBody ? { transition: headToBody, frames: headOverlap } : undefined}
            exit={end && bodyToEnd ? { transition: bodyToEnd, frames: endOverlap } : undefined}
          >
            <BodySection scene={body} legacyOriginalAudio={legacyOriginalAudio} previewProxyKey={previewProxyKey} />
          </TransitionWrapper>
        </Sequence>

        {end && layout.end ? (
          <Sequence from={layout.end.from} durationInFrames={layout.end.durationInFrames} name="END">
            <TransitionWrapper
              durationInFrames={layout.end.durationInFrames}
              enter={bodyToEnd ? { transition: bodyToEnd, frames: endOverlap } : undefined}
            >
              <BookendSection scene={end} />
            </TransitionWrapper>
          </Sequence>
        ) : null}

        {/* Captions and audio sit above the sections and run on the global clock,
            so they carry across transitions instead of being cut by them. */}
        <CaptionLayer captions={timeline.captions} />
        {/* `fps`, not `timeline.fps`.
            Audio start times are seconds and have to become frame numbers at
            the rate Remotion is actually counting at. The preview plays a 30fps
            proxy of a 60fps timeline, so using the timeline's own rate placed
            every clip at twice its frame index — narration slid further behind
            the picture the longer it played, while the render, where the two
            rates happen to match, was perfectly fine. */}
        <AudioTracks tracks={timeline.audio} fps={fps} />
      </AbsoluteFill>
    </AssetBaseContext.Provider>
  );
};

/**
 * Remotion asks for the composition's dimensions before rendering a frame. The
 * timeline is validated here as well, so a malformed document fails fast with a
 * useful message rather than part-way through an encode.
 */
export const calculateVeysoMetadata = ({ props }: { props: VeysoCompositionProps }) => {
  const timeline = timelineSchema.parse(props.timeline);
  const layout = computeLayout(timeline);
  return {
    durationInFrames: layout.totalFrames,
    fps: layout.fps,
    width: layout.width,
    height: layout.height,
    props: { ...props, timeline },
  };
};
